import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Package, DollarSign, Clock, CheckCircle, XCircle, 
  Search, Filter, Download, Eye, LogOut, Settings, Bell,
  TrendingUp, UserPlus, FileText, BarChart3
} from 'lucide-react';
import toast from 'react-hot-toast';
import NotificationSystem from '../components/NotificationSystem';
import AdvancedReports from '../components/AdvancedReports';

interface Estatisticas {
  total_clientes: number;
  total_fornecedores: number;
  total_pedidos: number;
  total_orcamentos: number;
  orcamentos_enviados: number;
  valor_total_pedidos: number;
  tempo_medio_resposta: number;
}

interface Pedido {
  id: number;
  cliente: string;
  tipo_equipamento: string;
  marca: string;
  modelo: string;
  status: string;
  data_criacao: string;
  orcamentos_count: number;
}

interface Usuario {
  id: number;
  nome: string;
  email: string;
  tipo: 'cliente' | 'fornecedor' | 'admin';
  data_cadastro: string;
  status: 'ativo' | 'inativo';
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [estatisticas, setEstatisticas] = useState<Estatisticas>({
    total_clientes: 0,
    total_fornecedores: 0,
    total_pedidos: 0,
    total_orcamentos: 0,
    orcamentos_enviados: 0,
    valor_total_pedidos: 0,
    tempo_medio_resposta: 0
  });
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reportData, setReportData] = useState<any>({});

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Buscar estatísticas do dashboard
      const response = await fetch('/api/admin/dashboard', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
          return;
        }
        throw new Error('Erro ao carregar dados');
      }

      const data = await response.json();
      
      setEstatisticas({
        total_clientes: data.clientes,
        total_fornecedores: data.fornecedores,
        total_pedidos: data.pedidos_pendentes,
        total_orcamentos: data.orcamentos,
        orcamentos_enviados: data.orcamentos,
        valor_total_pedidos: 0, // Será implementado futuramente
        tempo_medio_resposta: 0 // Será implementado futuramente
      });

      // Mock de pedidos
      const mockPedidos: Pedido[] = [
        {
          id: 1,
          cliente: 'Empresa ABC Ltda',
          tipo_equipamento: 'Escavadeira',
          marca: 'Caterpillar',
          modelo: '320D',
          status: 'orcamentos_recebidos',
          data_criacao: '2024-01-15',
          orcamentos_count: 3
        },
        {
          id: 2,
          cliente: 'Construções XYZ',
          tipo_equipamento: 'Pá Carregadeira',
          marca: 'Komatsu',
          modelo: 'WA380',
          status: 'pendente',
          data_criacao: '2024-01-14',
          orcamentos_count: 0
        },
        {
          id: 3,
          cliente: 'Mineração 123',
          tipo_equipamento: 'Trator',
          marca: 'John Deere',
          modelo: '750J',
          status: 'finalizado',
          data_criacao: '2024-01-10',
          orcamentos_count: 5
        }
      ];
      setPedidos(mockPedidos);

      // Mock de usuários
      const mockUsuarios: Usuario[] = [
        {
          id: 1,
          nome: 'João Silva',
          email: 'joao@empresa.com',
          tipo: 'cliente',
          data_cadastro: '2024-01-10',
          status: 'ativo'
        },
        {
          id: 2,
          nome: 'Maria Santos',
          email: 'maria@fornecedor.com',
          tipo: 'fornecedor',
          data_cadastro: '2024-01-08',
          status: 'ativo'
        },
        {
          id: 3,
          nome: 'Pedro Costa',
          email: 'pedro@cliente.com',
          tipo: 'cliente',
          data_cadastro: '2024-01-05',
          status: 'inativo'
        }
      ];
      setUsuarios(mockUsuarios);

      // Mock de dados para relatórios
      setReportData({
        overview: {
          totalRevenue: 1250000,
          totalOrders: 289,
          averageRating: 4.2,
          growthRate: 15.3
        },
        volumeByBrand: [
          { brand: 'Caterpillar', volume: 45 },
          { brand: 'Komatsu', volume: 32 },
          { brand: 'Volvo', volume: 28 },
          { brand: 'John Deere', volume: 25 }
        ],
        performance: {
          responseTime: 2.5,
          approvalRate: 78.5,
          customerSatisfaction: 4.2
        },
        satisfaction: {
          averageRating: 4.2,
          totalReviews: 156,
          ratingDistribution: [
            { rating: 5, count: 89 },
            { rating: 4, count: 45 },
            { rating: 3, count: 15 },
            { rating: 2, count: 5 },
            { rating: 1, count: 2 }
          ]
        },
        temporalEvolution: [
          { month: 'Jan', orders: 45, revenue: 180000 },
          { month: 'Feb', orders: 52, revenue: 210000 },
          { month: 'Mar', orders: 48, revenue: 195000 },
          { month: 'Apr', orders: 61, revenue: 245000 },
          { month: 'May', orders: 55, revenue: 220000 }
        ]
      });

    } catch (error) {
      toast.error('Erro ao carregar dados do dashboard');
      console.error('Erro:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    toast.success('Logout realizado com sucesso');
    navigate('/');
  };

  const handleExportReport = (data: any, filename: string) => {
    // Simular exportação de CSV
    const csvContent = "data:text/csv;charset=utf-8," + 
      Object.keys(data[0]).join(",") + "\n" +
      data.map((row: any) => Object.values(row).join(",")).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Relatório ${filename} exportado com sucesso!`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pendente':
        return 'text-yellow-600 bg-yellow-100';
      case 'orcamentos_recebidos':
        return 'text-green-600 bg-green-100';
      case 'finalizado':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pendente':
        return <Clock className="w-4 h-4" />;
      case 'orcamentos_recebidos':
        return <CheckCircle className="w-4 h-4" />;
      case 'finalizado':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'cliente':
        return 'text-blue-600 bg-blue-100';
      case 'fornecedor':
        return 'text-green-600 bg-green-100';
      case 'admin':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusUsuarioColor = (status: string) => {
    switch (status) {
      case 'ativo':
        return 'text-green-600 bg-green-100';
      case 'inativo':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo-pecaja.png" 
                alt="PeçaJá & Sobratema" 
                className="h-12 w-auto object-contain"
              />
            </div>

            <div className="flex items-center space-x-4">
              <NotificationSystem />
              <button 
                onClick={() => navigate('/configuracoes')}
                className="btn btn-outline"
              >
                <Settings className="w-4 h-4 mr-2" />
                Configurações
              </button>
              <button onClick={handleLogout} className="btn btn-outline">
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Dashboard Administrativo
          </h1>
          <p className="text-gray-600">
            Gerencie usuários, monitore pedidos e visualize relatórios
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-1 bg-white rounded-lg p-1 mb-8">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'dashboard'
                ? 'bg-red text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('usuarios')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'usuarios'
                ? 'bg-red text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Usuários
          </button>
          <button
            onClick={() => setActiveTab('pedidos')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'pedidos'
                ? 'bg-red text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Pedidos
          </button>
          <button
            onClick={() => setActiveTab('relatorios')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'relatorios'
                ? 'bg-red text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Relatórios
          </button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="stats-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total de Clientes</p>
                    <p className="text-2xl font-bold text-gray-900">{estatisticas.total_clientes}</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-500" />
                </div>
              </div>

              <div className="stats-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total de Fornecedores</p>
                    <p className="text-2xl font-bold text-gray-900">{estatisticas.total_fornecedores}</p>
                  </div>
                  <Package className="w-8 h-8 text-green-500" />
                </div>
              </div>

              <div className="stats-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total de Pedidos</p>
                    <p className="text-2xl font-bold text-gray-900">{estatisticas.total_pedidos}</p>
                  </div>
                  <Package className="w-8 h-8 text-yellow-500" />
                </div>
              </div>

              <div className="stats-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total de Orçamentos</p>
                    <p className="text-2xl font-bold text-gray-900">{estatisticas.total_orcamentos}</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-green-500" />
                </div>
              </div>
            </div>

            {/* Additional Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="stats-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Orçamentos Enviados</p>
                    <p className="text-2xl font-bold text-gray-900">{estatisticas.orcamentos_enviados}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-blue-500" />
                </div>
              </div>

              <div className="stats-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Valor Total dos Pedidos</p>
                    <p className="text-2xl font-bold text-gray-900">
                      R$ {estatisticas.valor_total_pedidos.toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <DollarSign className="w-8 h-8 text-green-500" />
                </div>
              </div>

              <div className="stats-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Tempo Médio de Resposta</p>
                    <p className="text-2xl font-bold text-gray-900">{estatisticas.tempo_medio_resposta}h</p>
                    <p className="text-xs text-gray-500 mt-1">Média dos fornecedores</p>
                  </div>
                  <Clock className="w-8 h-8 text-yellow-500" />
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Atividade Recente</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Novo cliente cadastrado: Empresa ABC Ltda</span>
                  <span className="text-xs text-gray-500 ml-auto">2 min atrás</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Orçamento aprovado para pedido #123</span>
                  <span className="text-xs text-gray-500 ml-auto">15 min atrás</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Novo fornecedor em análise: Peças Express</span>
                  <span className="text-xs text-gray-500 ml-auto">1h atrás</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Usuários Tab */}
        {activeTab === 'usuarios' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Gerenciar Usuários</h2>
              <button className="btn btn-primary">
                <UserPlus className="w-4 h-4 mr-2" />
                Novo Usuário
              </button>
            </div>

            <div className="card">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-medium text-gray-900">Nome</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900">E-mail</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900">Tipo</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900">Status</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900">Data Cadastro</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usuarios.map((usuario) => (
                      <tr key={usuario.id} className="border-b border-gray-100">
                        <td className="py-3 px-4">{usuario.nome}</td>
                        <td className="py-3 px-4 text-gray-600">{usuario.email}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTipoColor(usuario.tipo)}`}>
                            {usuario.tipo}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusUsuarioColor(usuario.status)}`}>
                            {usuario.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {new Date(usuario.data_cadastro).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex space-x-2">
                            <button className="btn btn-outline text-xs">
                              <Eye className="w-3 h-3 mr-1" />
                              Ver
                            </button>
                            <button className="btn btn-outline text-xs">
                              <Settings className="w-3 h-3 mr-1" />
                              Editar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Pedidos Tab */}
        {activeTab === 'pedidos' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Monitorar Pedidos</h2>
              <button className="btn btn-outline">
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </button>
            </div>

            <div className="space-y-4">
              {pedidos.map((pedido) => (
                <div key={pedido.id} className="card">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {pedido.tipo_equipamento} {pedido.marca} {pedido.modelo}
                          </h3>
                          <p className="text-gray-600 mb-2">
                            <span className="font-medium">Cliente:</span> {pedido.cliente}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(pedido.status)}`}>
                            <div className="flex items-center space-x-1">
                              {getStatusIcon(pedido.status)}
                              <span>
                                {pedido.status === 'pendente' && 'Pendente'}
                                {pedido.status === 'orcamentos_recebidos' && 'Com Orçamentos'}
                                {pedido.status === 'finalizado' && 'Finalizado'}
                              </span>
                            </div>
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4 sm:mb-0">
                          <span>Criado em: {new Date(pedido.data_criacao).toLocaleDateString('pt-BR')}</span>
                          <span>Orçamentos: {pedido.orcamentos_count}</span>
                        </div>

                        <div className="flex space-x-2">
                          <button className="btn btn-outline">
                            <Eye className="w-4 h-4 mr-2" />
                            Ver Detalhes
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Relatórios Tab */}
        {activeTab === 'relatorios' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Relatórios Avançados</h2>
              <button 
                onClick={() => handleExportReport(reportData.volumeByBrand, 'volume-por-marca')}
                className="btn btn-outline"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar Dados
              </button>
            </div>

            <AdvancedReports 
              data={reportData}
              onExport={handleExportReport}
            />
          </div>
        )}
      </div>

      {/* Settings Modal */}
      {/* Removed SettingsModal import, so this block is removed */}
    </div>
  );
};

export default AdminDashboard; 